<template>
    <div class="columns medium-6 large-4 mx-1">
        <section class="bg-white-95 p-1 pt-2 c-login-card">
            <!-- Login step is v-show (not v-if) so typed values and password-manager state survive a round trip -->
            <div v-show="step === 'login'">
                <slot></slot>

                <gwb-form-v2
                    :data="data"
                    :submit="loginUser"
                    :validation-rules="validationRules"
                >
                    <template #default="{ validator, touch }">
                        <gwb-float-field
                            v-model="data.email"
                            type="email"
                            id="email"
                            name="email"
                            :label="$lang('landing.signup_email')"
                            :error="validator.email.$error"
                            @change="touch(validator.email)"
                            @blur="touch(validator.email)"
                        ></gwb-float-field>
                        <span v-if="validator.email.$error" v-text="validator.email.$errors[0].$message" class="is-visible form-error"></span>

                        <gwb-float-field
                                v-model="data.password"
                                :type="passwordVisible ? 'text' : 'password'"
                                id="password"
                                name="password"
                                :label="$lang('landing.signup_pass')"
                                :error="validator.password.$error"
                                @change="touch(validator.password)"
                                @blur="touch(validator.password)"
                        >
                            <template #button>
                                <button
                                    id="login-form-show-pswd-btn"
                                    type="button"
                                    @click="passwordVisible = !passwordVisible"
                                    tabindex="-1"
                                >
                                    <span v-if="!passwordVisible"><svg class="icon double" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40"><path class="st0" d="M20.4 30.6c-5.3-.1-9.5-2-12.9-5.4-.6-.6-1.1-1.2-.4-2 .8-.8 1.4-.2 2.1.4 7.4 7 17.8 6.1 23.8-2.2.6-.8.7-1.4 0-2.2-6.9-9.6-19.9-9.2-26.3.6-.5.7-.8 2-2.1 1.3-1.3-.8-.3-1.7.2-2.6 7.5-11.7 23.4-11.5 30.7.4.5.7.7 1.4.2 2.2-3.6 5.9-8.7 9.2-15.3 9.5z"/><path class="st0" d="M25.7 20.2c0 3.2-2.6 5.8-5.7 5.8-3.2 0-5.8-2.6-5.8-5.7 0-3.2 2.5-5.8 5.7-5.8s5.8 2.5 5.8 5.7zm-2.2.1c0-1.9-1.5-3.5-3.4-3.5-1.8 0-3.4 1.5-3.4 3.3-.1 1.9 1.5 3.5 3.4 3.6 1.7 0 3.4-1.5 3.4-3.4z"/></svg></span>
                                    <span v-else="passwordVisible"><svg class="icon double" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40"><path class="st0" d="M20.4 29.7c-5.1-.1-9-1.9-12.4-5.2-.5-.5-1-1.2-.4-1.9.7-.8 1.4-.2 2 .4 7.1 6.7 17.1 5.8 22.7-2.1.5-.8.6-1.3 0-2.1-6.5-9.1-19-8.8-25.1.7-.5.7-.8 1.9-2 1.3-1.2-.8-.2-1.7.3-2.5 7.2-11.3 22.4-11.1 29.4.3.4.7.7 1.3.2 2.1-3.5 5.6-8.4 8.8-14.7 9z"/><path class="st0" d="M25.5 19.8c0 3-2.4 5.5-5.4 5.5-3 0-5.5-2.4-5.5-5.4 0-3 2.4-5.5 5.4-5.5 3-.1 5.5 2.4 5.5 5.4zm-2.2.1c0-1.8-1.5-3.3-3.3-3.3-1.8 0-3.2 1.4-3.3 3.2-.1 1.8 1.4 3.3 3.2 3.4 1.9 0 3.4-1.5 3.4-3.3z"/><path class="st0" d="M30.5 9.5c-.7-.7-1.8-.7-2.5 0l-19 19c-.7.7-.7 1.8 0 2.5s1.8.7 2.5 0l19-19c.7-.7.7-1.8 0-2.5z"/></svg></span>
                                </button>
                            </template>
                        </gwb-float-field>
                        <span v-if="validator.password.$error" v-text="validator.password.$errors[0].$message" class="is-visible form-error"></span>

                        <div class="row align-center align-middle">
                            <div class="columns medium-12">
                                <button
                                    type="submit"
                                    id="login-form-login-btn"
                                    :class="{ 'invalid': validator.$errors.length > 0 }"
                                    class="large expanded login button text-uppercase"
                                >
                                    <strong class="text-uppercase">{{ $lang('landing.login_button') }}</strong>
                                </button>
                            </div>
                        </div>

                        <div id="login-form-or-divider" class="c-login-divider">
                            <span>{{ $lang('db.login-code:divider') }}</span>
                        </div>

                        <div class="row align-center align-middle c-login-alt-row">
                            <div class="columns medium-12">
                                <button
                                    type="button"
                                    id="login-form-login-code-btn"
                                    class="large expanded login-code button"
                                    :class="{ 'is-long-label': $lang('db.login-code:button').length > 24 }"
                                    @click="openCodeRequest"
                                >
                                    <strong>{{ $lang('db.login-code:button') }}</strong>
                                </button>
                            </div>
                            <div class="columns medium-12 flex-initial sm:flex-auto">
                                <a
                                    id="login-form-login-facebook-btn"
                                    href="/auth/oauth-login/Facebook"
                                    class="large expanded facebook button"
                                    data-cookieconsent="ignore"
                                >
                                    <strong class="hide-for-small-only text-uppercase">{{ $lang('landing.login_button_fb') }}</strong>
                                    <svg class="show-for-small-only icon double" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40"><path class="st0" d="M24.4 6.3c0 1.5-.1 3-.1 4.5h-3.1c-.3 0-.6 0-.9.1-.5.1-.8.4-1 .9 0 .1 0 .2-.1.2 0 .2-.1.5-.1.7v3.5h5c-.1 1.6-.2 3.2-.2 4.8H19v13.2h-5.1V20.7h-3.5v-4.8h3.5V12c0-.8.1-1.6.4-2.4.6-1.5 1.6-2.6 3.1-3.2.6-.3 1.2-.4 1.9-.5.7-.1 1.5-.1 2.2-.1.5 0 1.1 0 1.6.1.5.3.8.3 1.3.4-.1 0-.1 0 0 0z"/></svg>
                                </a>
                            </div>
                            <div class="columns medium-12 flex-initial sm:flex-auto">
                                <a
                                    id="login-form-login-google-btn"
                                    href="/auth/oauth-login/Google"
                                    class="large expanded google button"
                                    data-cookieconsent="ignore"
                                >
                                    <strong class="hide-for-small-only text-uppercase">{{ $lang('landing.login_button_google') }}</strong>
                                    <svg class="show-for-small-only icon double" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40"><path class="st0" d="M19.8 9c.8 1 1.1 2.1 1.3 3.3.2 2.2-.6 3.9-2.2 5.3-.2.2-.5.4-.8.6l-.6.6c-.4.5-.4.9 0 1.4.5.5 1.1 1 1.6 1.5.4.4.8.7 1.2 1 1.2 1.1 1.8 2.5 1.9 4.1.1 2.9-1.3 5.2-4.2 6.4-1.3.6-2.7.8-4.1.9-2.1.1-4.2-.2-6.1-1.1-.6-.3-1.2-.7-1.7-1.1-1.7-1.4-2.1-3.3-1.2-5.2.9-1.9 2.4-3 4.2-3.8 1.2-.5 2.4-.7 3.7-.8h.4c-.4-.8-.5-1.6-.3-2.4-.6-.1-1.1-.2-1.7-.3-3.2-.8-5.2-3.5-5-6.8.1-1.5.8-2.7 1.8-3.7 1.6-1.6 3.6-2.5 5.9-2.5H22c.4 0 .6.1.8.5.1.3 0 .6-.4.8-.5.3-1 .7-1.4 1-.3.2-.6.4-1 .3h-.2zM14 31.5h.8c1-.1 1.9-.3 2.7-.8 1.5-.9 2.1-3.6.3-4.8-.6-.4-1.2-.9-1.9-1.3-.1 0-.1-.1-.2-.1-2.1-.5-4-.2-5.7 1.1-.5.4-1 1-1.1 1.7-.3 1.3.2 2.4 1.2 3.2 1.1.8 2.5 1.1 3.9 1zm-3.5-19.3c0 .3 0 .7.1 1 .3 1.6 1 2.9 2.4 3.9.7.5 1.5.6 2.3.3.8-.3 1.2-.9 1.4-1.7.5-1.3.3-2.6-.2-3.8-.5-1.2-1.2-2.2-2.4-2.8-1.3-.6-2.6-.2-3.2 1.2-.3.5-.4 1.2-.4 1.9z"/><path class="st0" d="M30.2 17.6h4.4c.6 0 .8.2.8.8v1c0 .5-.2.8-.8.8h-4.5v4.5c0 .6-.2.8-.9.8h-.8c-.9 0-1-.2-1-1v-4.3h-4.5c-.6 0-.8-.2-.8-.8v-1c0-.5.2-.8.8-.8h4.5v-4.5c0-.6.2-.8.8-.8h1c.5 0 .8.2.8.8v4.1c.2.2.2.3.2.4z"/></svg>
                                </a>
                            </div>
                        </div>
                    </template>
                </gwb-form-v2>
            </div>

            <!-- Request step: ask for the code -->
            <form
                v-if="step === 'request'"
                id="login-code-request-form"
                novalidate
                @submit.prevent="sendCode"
            >
                <p class="tc-purple text-center lead bold">{{ $lang('db.login-code:request:title') }}</p>
                <p class="text-center bold c-login-code-request-body">{{ $lang('db.login-code:request:body') }}</p>

                <gwb-float-field
                    v-model="data.email"
                    type="email"
                    id="login-code-email"
                    name="login_code_email"
                    :label="$lang('landing.signup_email')"
                ></gwb-float-field>
                <span
                    v-if="sendError"
                    id="login-code-send-error"
                    class="is-visible form-error"
                    v-text="sendError"
                />

                <button
                    type="submit"
                    id="login-code-send-btn"
                    class="large expanded login button text-uppercase mt-1"
                    :class="{ 'invalid': !canSendCode }"
                    :disabled="!canSendCode || sending"
                >
                    <strong class="text-uppercase">{{ $lang('db.login-code:request:send') }}</strong>
                </button>

                <a
                    id="login-code-back-link"
                    class="block text-center mt-1"
                    href="#"
                    @click.prevent="backToLogin"
                >
                    <small class="semibold">{{ $lang('db.login-code:back') }}</small>
                </a>
            </form>

            <!-- Sent step (PLAT-85): the code screen. Errors sit under the boxes and never clear the digits. -->
            <form
                v-if="step === 'sent'"
                id="login-code-sent"
                novalidate
                @submit.prevent="verifyCode"
            >
                <p class="c-login-code-title">
                    {{ $lang('db.login-code:sent:title') }}
                </p>
                <div id="login-code-panel" class="c-login-code-panel">
                    {{ panelParts.before }}
                    <br v-if="panelParts.before" />
                    <b class="c-login-code-panel__address">{{ data.email }}</b>
                    {{ panelParts.after }}
                </div>

                <one-time-code-input
                    ref="codeInput"
                    v-model="code"
                    class="c-login-code-boxes"
                    input-id="login-code-input"
                    described-by="login-code-panel login-code-error"
                    @input="onCodeEdited"
                />
                <div
                    v-if="codeError"
                    id="login-code-error"
                    class="c-login-code-error"
                    role="alert"
                >
                    {{ codeError }}
                </div>

                <div v-if="cooldownLeft > 0" class="c-login-code-limit">
                    <div id="login-code-resend" class="c-login-code-limit__resend">
                        {{ $lang('db.login-code:resend:prompt') }}
                        <button
                            id="login-code-resend-btn"
                            type="button"
                            class="c-login-code-resend is-locked"
                            disabled
                            aria-disabled="true"
                        >
                            {{ $lang('db.login-code:resend:action') }}
                        </button>
                    </div>
                    <div
                        id="login-code-rate-limited"
                        class="c-login-code-limit__error"
                        role="alert"
                    >
                        {{ $lang('db.login-code:rate-limited', { n: cooldownMinutesLeft }) }}
                    </div>
                </div>
                <div v-else id="login-code-resend-row" class="c-login-code-resend-row">
                    {{ $lang('db.login-code:resend:prompt') }}
                    <span
                        v-if="resendWaitLeft > 0"
                        id="login-code-resend-waiting"
                        class="c-login-code-resend is-locked"
                    >{{ $lang('db.login-code:resend:waiting', { timer: resendTimer }) }}</span>
                    <button
                        v-else
                        id="login-code-resend-active-btn"
                        type="button"
                        class="c-login-code-resend"
                        :disabled="resending"
                        @click="resendCode"
                    >
                        {{ $lang('db.login-code:resend:action') }}
                    </button>
                </div>

                <button
                    id="login-code-verify-btn"
                    type="submit"
                    class="large expanded login button text-uppercase c-login-code-submit"
                    :class="{ 'invalid': !codeComplete }"
                    :disabled="!codeComplete || verifying"
                >
                    <strong class="text-uppercase">{{ $lang('landing.login_button') }}</strong>
                </button>

                <a
                    id="login-code-sent-back-link"
                    class="c-login-code-back"
                    href="#"
                    @click.prevent="backToLogin"
                >
                    {{ $lang('db.login-code:back') }}
                </a>
            </form>

            <div v-show="step === 'login'" class="c-login-below-card">
                <p class="text-center semibold mb-0">
                    {{ $lang('landing.login_no_account') }}
                    <a id="login-form-signup-link" href="/signup">{{ $lang('landing.login_go_to_signup') }}</a>
                </p>

                <a
                    id="login-form-forgot-password-link"
                    class="mt-1 block text-center"
                    :href="remindPasswordLink(canSendCode ? data.email : null)"
                >
                    <small class="semibold">{{ $lang('landing.login_forgot_link') }}</small>
                </a>
            </div>
        </section>
    </div>
</template>
<script>
import {
    required, email, minLength, apiError, helpers,
} from 'gwbui/src/components/form-v2/validators';
import GwbFloatField from './gwb-float-field.vue';
import OneTimeCodeInput from './one-time-code-input.vue';
import { isCompleteCode } from '../one-time-code';
import {
    STEP_LOGIN,
    isSendableEmail,
    openRequest,
    backToLogin,
    requestSucceeded,
    requestLimited,
    cooldownSecondsLeft,
    cooldownMinutes,
    sendFailureOutcome,
    resendCountdown,
    secondsLeft,
    formatTimer,
    verifyFailureOutcome,
} from '../login-code-flow';

export default {

    name: 'LoginForm',

    components: {
        GwbFloatField,
        OneTimeCodeInput,
    },

    props: {
        action: {
            type: String,
            required: true,
        },
        codeAction: {
            type: String,
            default: '',
        },
        verifyAction: {
            type: String,
            default: '',
        },
        screenLocale: {
            type: String,
            default: '',
        },
    },

    data() {
        return {
            data: {
                email: '',
                password: '',
            },
            passwordVisible: false,
            step: STEP_LOGIN,
            sending: false,
            sendError: '',
            cooldownEndsAt: null,
            now: Date.now(),
            cooldownTimer: null,
            code: '',
            codeError: '',
            verifying: false,
            resending: false,
            resendEndsAt: null,
            resendIsCapWait: false,
            resendTimerId: null,
        };
    },

    computed: {
        cooldownLeft() {
            return cooldownSecondsLeft({ cooldownEndsAt: this.cooldownEndsAt }, this.now);
        },

        cooldownMinutesLeft() {
            return cooldownMinutes(this.cooldownLeft);
        },

        codeComplete() {
            return isCompleteCode(this.code);
        },

        /** [C7] with the address lifted out, so the address can sit on its own line in bold (prototype panel). */
        panelParts() {
            const marker = '\u0000';
            const text = String(this.$lang('db.login-code:sent:panel', { email: marker }));
            const [before = '', after = ''] = text.split(marker);
            return { before: before.trim(), after };
        },

        resendWaitLeft() {
            return secondsLeft(this.resendEndsAt, this.now);
        },

        resendTimer() {
            return formatTimer(this.resendWaitLeft);
        },

        canSendCode() {
            return isSendableEmail(this.data.email);
        },

        // Messages live in the rules (SUP-346); the email rules are the same barrel validators
        // login-code-flow's EMAIL_RULES uses, so the request step accepts exactly what LOG IN does.
        validationRules() {
            return {
                email: {
                    required: helpers.withMessage(this.$lang('landing.form_error_4'), required),
                    email: helpers.withMessage(this.$lang('landing.form_error_2'), email),
                    apiError: apiError('email'),
                },
                password: {
                    required: helpers.withMessage(this.$lang('landing.form_error_3'), required),
                    minLength: helpers.withMessage(this.$lang('landing.form_error_3'), minLength(5)),
                    apiError: apiError('password'),
                },
            }
        },
    },

    watch: {
        step(next) {
            if (next === 'sent') this.focusCode();
        },
    },

    mounted() {
        gtmGaEvent('Login', 'Login form was loaded', null, 0);
    },

    beforeDestroy() {
        this.clearCooldown();
        this.clearResend();
    },

    methods: {
        /** Moves between steps; every transition keeps data.email (AC-8, AC-12). */
        goTo(next) {
            this.step = next.step;
        },

        openCodeRequest() {
            this.goTo(openRequest({ step: this.step, email: this.data.email }));
        },

        backToLogin() {
            this.sendError = '';
            this.code = '';
            this.codeError = '';
            this.clearCooldown();
            this.clearResend();
            this.goTo(backToLogin({ step: this.step, email: this.data.email }));
        },

        sendCode() {
            if (!this.canSendCode || this.sending) return Promise.resolve();

            this.sending = true;
            this.sendError = '';
            return this.$http.post(this.codeAction, { email: this.data.email, screen_locale: this.screenLocale })
                .then((response) => {
                    this.codeSent(response);
                    this.goTo(requestSucceeded({ step: this.step, email: this.data.email }));
                })
                .catch((err) => {
                    const outcome = sendFailureOutcome(err);
                    if (outcome.kind === 'limited') {
                        this.startCooldown(outcome.seconds);
                        return;
                    }
                    // Stay on the request step, button re-enabled below.
                    this.sendError = this.$lang('shared.error:generic');
                })
                .then(() => {
                    this.sending = false;
                });
        },

        /** A new code is out: old digits are dead (AC-13); boxes start empty, countdown restarts, focus moves. */
        codeSent(response) {
            this.code = '';
            this.codeError = '';
            this.startResendCountdown(response && response.data ? response.data.resend_wait_seconds : 0);
            this.focusCode();
        },

        focusCode() {
            this.$nextTick(() => {
                if (this.$refs.codeInput) this.$refs.codeInput.focus();
            });
        },

        startResendCountdown(waitSeconds) {
            this.clearResend();
            this.now = Date.now();
            const next = resendCountdown(waitSeconds, this.now);
            this.resendEndsAt = next.endsAt;
            this.resendIsCapWait = next.isCapWait;
            this.resendTimerId = setInterval(() => {
                this.now = Date.now();
                if (this.resendWaitLeft <= 0) this.clearResend();
            }, 1000);
        },

        clearResend() {
            if (this.resendTimerId) clearInterval(this.resendTimerId);
            this.resendTimerId = null;
            this.resendEndsAt = null;
            this.resendIsCapWait = false;
        },

        /** An expired / locked-out code makes the per-send countdown pointless; a cap wait or cooling-off stays. */
        cancelSoftCountdown() {
            if (!this.resendIsCapWait) this.clearResend();
        },

        onCodeEdited() {
            this.codeError = '';
        },

        verifyCode() {
            if (!this.codeComplete || this.verifying) return Promise.resolve();

            this.verifying = true;
            this.codeError = '';
            return this.$http.post(this.verifyAction, { email: this.data.email, code: this.code })
                .then((response) => {
                    if (response.data.status === 'success' && response.data.newPage) {
                        // Stay in the verifying state: the page is leaving, a second submit must not race it.
                        window.location.assign(response.data.newPage + window.location.hash);
                        return;
                    }
                    this.codeError = this.$lang('shared.error:generic');
                    this.verifying = false;
                })
                .catch((err) => {
                    const outcome = verifyFailureOutcome(err);
                    if (outcome.kind === 'incorrect') {
                        this.codeError = this.$lang('db.login-code:error:incorrect');
                    } else if (outcome.kind === 'expired') {
                        this.codeError = this.$lang('db.login-code:error:expired');
                        this.cancelSoftCountdown();
                    } else {
                        this.codeError = this.$lang('shared.error:generic');
                    }
                    // The digits stay: the person edits one and tries again (AC-10, AC-11).
                    this.verifying = false;
                });
        },

        /** Resend is the request endpoint again: it carries PLAT-87's limiter, bot check and [C14]. */
        resendCode() {
            if (this.resending) return Promise.resolve();

            this.resending = true;
            return this.$http.post(this.codeAction, { email: this.data.email, screen_locale: this.screenLocale })
                .then((response) => {
                    this.codeSent(response);
                })
                .catch((err) => {
                    const outcome = sendFailureOutcome(err);
                    if (outcome.kind === 'limited') {
                        this.startCooldown(outcome.seconds);
                        return;
                    }
                    this.codeError = this.$lang('shared.error:generic');
                })
                .then(() => {
                    this.resending = false;
                });
        },

        /** PLAT-87: show [C14] and the locked resend row until the server-given cooling-off ends. */
        startCooldown(retryAfterSeconds) {
            this.clearCooldown();
            this.now = Date.now();
            const next = requestLimited({ step: this.step, email: this.data.email }, retryAfterSeconds, this.now);
            this.cooldownEndsAt = next.cooldownEndsAt;
            this.goTo(next);
            this.cooldownTimer = setInterval(() => {
                this.now = Date.now();
                if (this.cooldownLeft <= 0) this.clearCooldown();
            }, 1000);
        },

        clearCooldown() {
            if (this.cooldownTimer) clearInterval(this.cooldownTimer);
            this.cooldownTimer = null;
            this.cooldownEndsAt = null;
        },

        loginUser() {
            return this.$http.post(this.action, this.data)
                .then((response) => {
                    if (response.data.status === 'success' && response.data.newPage) {
                        window.location.assign(response.data.newPage + window.location.hash);
                    }
                    return response;
                });
        },

        remindPasswordLink(email) {
            let link = '/password/remind';
            if (email) link += `?email=${encodeURIComponent(email)}`;
            return link;
        },
    },

};
</script>
